import { getLogger } from '@logtape/logtape';
import { type PhoneNumber as LibPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js/max';
import z from 'zod';

import { DayjsUnit } from '@/models/Dayjs.ts';
import { GenericPatient, PatientInfoLine, PhoneNumber, PhoneNumberType } from '@/models/GenericPatient.ts';
import { IndicatorStatus } from '@/models/IndicatorStatus.ts';
import {
    CreateDocCirrusPatient,
    EditPatientFormData,
    NormalizedPatient,
    Patient,
    PatientFormData,
    UpdatePatient,
} from '@/models/Patient.ts';
import { Appointment, AppointmentBookStatus, DailyAppointment, Insurance } from '@/models/appointment/Appointment.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { CreatableDataCurUser, EnrichedDataCurUser } from '@/models/data-cur/DataCurUser.ts';
import { Document } from '@/models/documents/Document.ts';
import { InsuranceType } from '@/models/insurance.ts';

import { getUserAppointments } from '@/services/appointmentService.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';
import { useConfigStore } from '@/stores/configStore.ts';

import { getInstanceConfig } from '@/utils/configUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { getInsuranceStatus } from '@/utils/insurance-utils.ts';

import { isFutureAppointment, isPastAppointment } from './appointmentUtils.ts';
import { countryCodeToGermanName } from './localeUtils.ts';

const logger = getLogger(['util', 'patient']);

const ID_SEPARATOR = '::';

const GERMAN_COUNTRY_CODE = '+49';

const GERMAN_COUNTRY_CODE_ISO = 'DE';

export const resolvePatientUserId = (patient: GenericPatient): string | undefined => {
    return z.guid().safeParse(patient.eterno_user_id).success
        ? patient.eterno_user_id
        : patient.pvs_user_id || undefined;
};

/**
 * If userId represents an ETERNO user id, we use it as the patient id.
 * Otherwise we rebuild it with the current pmsPatientId since the userId
 */
export const resolveUserId = (
    userId: string | undefined,
    instanceId: string,
    pmsPatientId: string
): { id: string; isEternoUser: boolean } => {
    if (userId && z.guid().safeParse(userId).success) return { id: userId, isEternoUser: true };
    return { id: `${instanceId}${ID_SEPARATOR}${pmsPatientId}`, isEternoUser: false };
};

export const formatPatientAddress = (address?: GenericPatient['address']): string | undefined => {
    if (!address) return undefined;

    const streetLine = [address.street, address.house_number]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(' ');
    const localityLine = [address.zip, address.city]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(' ');
    const addressDetails = address.addon?.trim();
    const country = countryCodeToGermanName(address.country_code)?.trim();

    const parts = [streetLine, addressDetails, localityLine, country].filter((part) => !!part);

    return parts.length > 0 ? parts.join(', ') : undefined;
};

/**
 * Sanitizes a phone number for parsing: trims surrounding whitespace and, for German
 * patients (`country_code === 'DE'`), applies a trunk-prefix workaround where a single
 * leading 0 (national trunk prefix) is replaced with the +49 country code so the number
 * can be parsed and sent.
 * The international prefix '00' is left untouched to avoid corrupting the country code.
 */
const sanitizePhoneNumber = (phone: string, country_code?: string): string => {
    const trimmed = phone.trim();
    if (country_code !== GERMAN_COUNTRY_CODE_ISO) {
        return trimmed;
    }
    if (trimmed.startsWith('0') && !trimmed.startsWith('00')) {
        return `${GERMAN_COUNTRY_CODE}${trimmed.slice(1)}`;
    }
    return trimmed;
};

/**
 * Normalizes an international phone number to E.164 format (e.g., +4915123456789).
 * Pass `country_code` so the German trunk-prefix workaround only runs for DE patients.
 */
export const normalizePhoneNumber = (phone: string, country_code?: string): string => {
    if (!phone) {
        return '';
    }

    const phone_number: LibPhoneNumber | undefined = parsePhoneNumberFromString(
        sanitizePhoneNumber(phone, country_code)
    );
    if (!phone_number) {
        return '';
    }
    return phone_number.number;
};

/**
 * Parses and validates a phone number, returning its libphonenumber type.
 * Applies the German trunk-prefix workaround and requires a leading `+` and a valid number.
 * Returns `undefined` when the number is empty, unparseable, or invalid.
 * Pass `country_code` so the German trunk-prefix workaround only runs for DE patients.
 */
const getValidatedPhoneNumberType = (
    phone: string,
    country_code?: string
): ReturnType<LibPhoneNumber['getType']> | undefined => {
    if (!phone) {
        return undefined;
    }

    const candidate = sanitizePhoneNumber(phone, country_code);
    if (!candidate.startsWith('+')) {
        return undefined;
    }

    const parsed: LibPhoneNumber | undefined = parsePhoneNumberFromString(candidate);
    if (!parsed?.isValid()) {
        return undefined;
    }

    return parsed.getType();
};

/**
 * Validates that a phone number is a mobile number in E.164 format.
 * Pass `country_code` so the German trunk-prefix workaround only runs for DE patients.
 */
export const isMobilePhoneNumber = (phone: string, country_code?: string): boolean => {
    const type = getValidatedPhoneNumberType(phone, country_code);
    return type === 'MOBILE' || type === 'FIXED_LINE_OR_MOBILE';
};

export const isLandlinePhoneNumber = (phone: string, country_code?: string): boolean => {
    const type = getValidatedPhoneNumberType(phone, country_code);
    return type === 'FIXED_LINE' || type === 'FIXED_LINE_OR_MOBILE';
};

export const getPreferredPhoneNumberByType = (
    phone_numbers: PhoneNumber[] | undefined,
    type: PhoneNumberType
): string | undefined => {
    const numbers_of_type = phone_numbers?.filter((phone) => phone.type === type) ?? [];
    const preferred = numbers_of_type.find((phone) => phone.is_preferred);
    return (preferred ?? numbers_of_type[0])?.value;
};

export const getPatientIdFromUser = (user: NormalizedPatient | EnrichedDataCurUser): string | undefined => {
    if ('user_id' in user) {
        return user.user_id;
    } else if ('id' in user) {
        return user.id;
    }
    return undefined;
};

// insurance_coverage_type being returned as lowercase on dev while it's returned as uppercase on prod
export const getInsuranceByKey = (key?: string): Insurance | undefined => {
    if (!key) return undefined;

    return Insurance[key.toUpperCase() as keyof typeof Insurance];
};

// Maps the provider-specific gender values (DocCirrus: MALE/FEMALE/VARIOUS/UNDEFINED/UNKNOWN, DataCur: Herr/Frau/Divers)
// to the KBV short codes: M männlich, W weiblich, D divers, X unbestimmt, U unbekannt.
const GENDER_CODES: Record<string, string> = {
    FEMALE: 'W',
    FRAU: 'W',
    MALE: 'M',
    HERR: 'M',
    VARIOUS: 'D',
    DIVERS: 'D',
    UNDEFINED: 'X',
    UNKNOWN: 'U',
};

export const getGenderCode = (gender?: string): string => (gender ? (GENDER_CODES[gender.toUpperCase()] ?? '') : '');

export const getPatientInfoLine = (patient?: GenericPatient): PatientInfoLine => {
    const dateOfBirth = patient?.personal.date_of_birth;
    const formattedDateOfBirth = dateOfBirth ? dayjs(dateOfBirth).format('DD.MM.YYYY') : '';
    const age = dateOfBirth ? dayjs().diff(dayjs(dateOfBirth), DayjsUnit.YEAR) : undefined;
    const dateOfBirthWithAge =
        formattedDateOfBirth && age !== undefined ? `${formattedDateOfBirth} (${age})` : formattedDateOfBirth;
    const genderCode = getGenderCode(patient?.personal.gender);
    const activeByInsurance = new Map<Insurance, boolean>();

    for (const insurance of patient?.insurances ?? []) {
        const key = getInsuranceByKey(insurance.type);
        if (!key) continue;

        const isCardActive = getInsuranceStatus(insurance) === IndicatorStatus.ACTIVE;
        activeByInsurance.set(key, (activeByInsurance.get(key) ?? false) || isCardActive);
    }

    const costCarriers = [...activeByInsurance].map(([insurance, isCardActive]) => ({ insurance, isCardActive }));

    return { dateOfBirthWithAge, genderCode, costCarriers };
};

export const filterPatientDocuments = (
    docs: Document[] | undefined,
    withAppointmentId: boolean,
    appointmentId?: string
): Document[] | undefined => {
    if (!docs) return undefined;
    if (withAppointmentId && !appointmentId) return [];

    return docs.filter((document) =>
        withAppointmentId ? document.appointment_id === appointmentId : !document.appointment_id
    );
};

export const buildInstanceUserId = (instanceId?: string, pmsUserId?: string): string => {
    if (!instanceId || !pmsUserId) {
        throw new Error('buildInstanceUserId requires both instanceId and pmsUserId');
    }
    return `${instanceId}${ID_SEPARATOR}${pmsUserId}`;
};

/**
 * Returns the correct user_id to use with any backend API based on the appointment.
 * It generates:
 * - for registered users (GUID-format id from Cognito, not necessarily a strict RFC UUID): returns the user_id unchanged — the backend APIs always expect the real Eterno user id for registered users
 * - for unregistered DocCirrus users: a consolidated instance-based identifier (`dc_instance_id::patient_id`) derived from the appointment link fields
 * - for unregistered DataCur users: a consolidated instance-based identifier (`instance_id::patient_id`) derived from the appointment data_cur_links fields
 * - fallback: returns the user_id as is when link fields are absent
 */
export const getUnregisteredUserId = (appointment: DailyAppointment): string => {
    const isRegisteredUser = z.guid().safeParse(appointment.user_id).success;

    if (isRegisteredUser) {
        return appointment.user_id;
    }

    if (
        appointment.pvs_provider === PVSProvider.DOC_CIRRUS &&
        appointment.links?.dc_instance_id &&
        appointment.links?.patient_id
    ) {
        return buildInstanceUserId(appointment.links.dc_instance_id, appointment.links.patient_id);
    }

    if (
        appointment.pvs_provider === PVSProvider.DATA_CUR &&
        appointment.data_cur_links?.instance_id &&
        appointment.data_cur_links?.patient_id
    ) {
        return buildInstanceUserId(appointment.data_cur_links.instance_id, appointment.data_cur_links.patient_id);
    }

    return appointment.user_id;
};

/** What the PMS writes into the patient slot of a schedule that has nobody attached. */
export const MISSING_PATIENT_ID = '-';

/** Whether the user_id identifies a patient; a schedule with nobody attached has an empty or placeholder segment. */
export const hasResolvablePatientId = (appointment: DailyAppointment): boolean => {
    const userId = getUnregisteredUserId(appointment);
    if (!userId) return false;

    const separatorIndex = userId.indexOf(ID_SEPARATOR);
    if (separatorIndex === -1) return true;

    const pmsPatientId = userId.slice(separatorIndex + ID_SEPARATOR.length);
    return !!pmsPatientId && pmsPatientId !== MISSING_PATIENT_ID;
};

export const transformPatientFormDataToDocCirrusPatient = (
    values: PatientFormData,
    is_data_admission_to_pvs_approved: boolean
): CreateDocCirrusPatient => ({
    user: {
        personal: {
            salutation: values.salutation,
            gender: values.gender ?? '', // always send gender explicitly
            title: values.academic_title,
            first_name: values.first_name,
            last_name: values.last_name,
            date_of_birth: dayjs(values.date_of_birth, 'DD.MM.YYYY').format('YYYY-MM-DD'),
            email: values.email,
            phone: normalizePhoneNumber(values.phone ?? '', values.country_code) || undefined,
            landline_phone: normalizePhoneNumber(values.landline_phone ?? '', values.country_code) || undefined,
        },
        contact: {
            street: values.street,
            house_number: values.house_number,
            addon: values.addon || undefined,
            zip: values.postal_code.toString(),
            city: values.city,
            country_code: values.country_code,
        },
        health_insurances: [
            {
                insurance_type: values.insurance_type,
                location_id: values.location_id,
            },
        ],
    },
    location_id: values.location_id,
    is_data_admission_to_pvs_approved: is_data_admission_to_pvs_approved,
});

export const transformPatientFormDataToDataCurPatient = (values: PatientFormData): CreatableDataCurUser => {
    return {
        instance_id: values.instance_id,
        birth_date: dayjs(values.date_of_birth, 'DD.MM.YYYY').format('YYYY-MM-DD'),
        name: {
            first_name: values.first_name,
            last_name: values.last_name,
        },
        contact: {
            email: values.email,
            phone: normalizePhoneNumber(values.phone ?? '', values.country_code) || undefined,
            address: {
                street: values.street ?? '',
                house_number: values.house_number,
                zip: values.postal_code,
                city: values.city,
                country_code: values.country_code,
            },
        },
        health_insurance: {
            // TODO: Figure out what to do with this field
            name: '',
            is_privately_insured: values.insurance_type === InsuranceType.PRIVATE,
        },
    };
};

export const updateCurrentPatient = (patient?: GenericPatient): void => {
    useBookingStore.setState({
        current_patient: patient,
    });
};

export const transformFormDataToUpdatePatient = (
    form_data: EditPatientFormData,
    insurance_id: string
): UpdatePatient => ({
    personal: {
        salutation: form_data.salutation,
        gender: form_data.gender,
        title: form_data.academic_title,
        first_name: form_data.first_name,
        last_name: form_data.last_name,
        date_of_birth: dayjs(form_data.date_of_birth, 'DD.MM.YYYY').format('YYYY-MM-DD'),
        email: form_data.email,
        phone: normalizePhoneNumber(form_data.phone ?? '', form_data.country_code) || undefined,
        landline_phone: normalizePhoneNumber(form_data.landline_phone ?? '', form_data.country_code) || undefined,
    },
    contact: {
        street: form_data.street,
        house_number: form_data.house_number,
        addon: form_data.addon,
        city: form_data.city,
        zip: form_data.postal_code,
        country_code: form_data.country_code,
    },
    insurances: [
        {
            id: insurance_id,
            type: form_data.insurance_type,
            name: form_data.insurance_name,
        },
    ],
});

const extractPhoneNumbersFromDCUser = (patient?: Patient): PhoneNumber[] => {
    if (!patient || !patient.communications) return [];

    const phone_numbers = [];

    for (const comm of patient.communications) {
        if (comm.type.includes('MOBILE') || comm.type.includes('PHONE')) {
            phone_numbers.push({
                type: comm.type.includes('MOBILE') ? PhoneNumberType.MOBILE : PhoneNumberType.LANDLINE,
                is_preferred: comm.preferred ?? comm.type === 'MOBILEPRIV',
                value: comm.value,
            });
        }
    }

    return phone_numbers;
};

export const convertUserToGenericPatient = (
    user: Patient | EnrichedDataCurUser,
    pvs_provider: PVSProvider
): GenericPatient | undefined => {
    switch (pvs_provider) {
        case PVSProvider.DOC_CIRRUS: {
            const patient = user as Patient;
            const phone_numbers = extractPhoneNumbersFromDCUser(patient);
            return {
                pvs_user_id: patient.user_id,
                is_registered: patient.is_registered ?? z.guid().safeParse(patient.eterno_user_id).success,
                eterno_user_id: patient.is_registered
                    ? patient.eterno_user_id
                    : buildInstanceUserId(patient.instance_id, patient.user_id),
                instance_id: patient.instance_id,
                patient_number: patient.account.patient_number,
                is_data_admission_to_pvs_approved: patient.is_data_admission_to_pvs_approved,
                pvs_provider: pvs_provider,
                personal: {
                    academic_title: patient.name.title,
                    salutation: patient.name.salutation,
                    first_name: patient.name.first_name,
                    last_name: patient.name.last_name,
                    date_of_birth: patient.account.date_of_birth,
                    gender: patient.name.gender,
                },
                address: {
                    house_number: '', // TODO: populate correctly once supported
                    ...patient.addresses[0],
                },
                contact: {
                    emails: patient.contact.emails.map((email, index) => ({
                        is_preferred: index === 0,
                        value: email,
                    })),
                    phone_numbers: phone_numbers,
                },
                insurances: patient.insurances.map((insurance) => ({
                    id: insurance.id,
                    name: insurance.name,
                    type: insurance.type,
                    location_id: insurance.location_id,
                    card_swipe_date: insurance.card_swipe_date,
                })),
            };
        }
        case PVSProvider.DATA_CUR: {
            const patient = user as EnrichedDataCurUser;
            const phone_numbers = [];
            if (patient.contact.mobile) {
                phone_numbers.push({ type: PhoneNumberType.MOBILE, is_preferred: true, value: patient.contact.mobile });
            }
            if (patient.contact.phone) {
                phone_numbers.push({
                    type: PhoneNumberType.LANDLINE,
                    is_preferred: !patient.contact.mobile,
                    value: patient.contact.phone,
                });
            }
            if (patient.contact.phone_alt) {
                phone_numbers.push({
                    type: PhoneNumberType.LANDLINE,
                    is_preferred: false,
                    value: patient.contact.phone_alt,
                });
            }
            const has_insurance =
                patient.health_insurance.name !== null && patient.health_insurance.is_privately_insured !== null;
            return {
                pvs_user_id: patient.id,
                eterno_user_id: patient.is_registered
                    ? patient.eterno_user_id
                    : buildInstanceUserId(patient.instance_id, patient.id),
                is_registered: patient.is_registered ?? z.guid().safeParse(patient.eterno_user_id).success,
                instance_id: patient.instance_id,
                patient_number: patient.number,
                pvs_provider: pvs_provider,
                personal: {
                    salutation: patient.name.title,
                    first_name: patient.name.first_name,
                    last_name: patient.name.last_name,
                    date_of_birth: patient.birth_date,
                },
                address: patient.contact.address && {
                    ...patient.contact.address,
                    house_number: patient.contact.address?.house_number ?? '',
                    country: countryCodeToGermanName(patient.contact.address?.country_code) ?? '',
                    country_code: patient.contact.address?.country_code ?? '',
                },
                contact: {
                    emails: [{ is_preferred: true, value: patient.contact.email }],
                    phone_numbers: phone_numbers,
                },
                insurances: has_insurance
                    ? [
                          {
                              name: patient.health_insurance.name,
                              type: patient.health_insurance.is_privately_insured ? 'PRIVATE' : 'PUBLIC',
                          },
                      ]
                    : [],
            };
        }
        default: {
            return undefined;
        }
    }
};

export const convertGenericPatientToDailyAppointmentPatient = (
    patient: GenericPatient,
    appointment: Appointment // Used to get the correct user_id
): DailyAppointment['patient'] => {
    if (!patient) return undefined;

    // Get primary email and phone if available
    const primary_email = patient.contact.emails.find((email) => email.is_preferred)?.value ?? '';
    const primary_phone = patient.contact.phone_numbers.find((phone) => phone.is_preferred)?.value ?? '';

    // Get primary insurance if available
    const primary_insurance = patient.insurances?.[0];

    return {
        id: appointment.user_id,
        name: {
            first_name: patient.personal.first_name,
            last_name: patient.personal.last_name,
            salutation: patient.personal.salutation || 'NONE',
            gender: patient.personal.gender || 'UNKNOWN',
        },
        account: {
            date_of_birth: patient.personal.date_of_birth,
            patient_number: patient.patient_number,
        },
        pvs: {
            instance_id: patient.instance_id,
            user_id: patient.pvs_user_id,
        },
        contact: {
            email: primary_email,
            phone: primary_phone,
        },
        address: patient.address
            ? {
                  id: '', // No direct mapping, using empty string
                  type: 'OFFICIAL',
                  street: patient.address.street,
                  house_number: patient.address.house_number,
                  addon: patient.address.addon,
                  city: patient.address.city,
                  zip: patient.address.zip,
                  country: patient.address.country,
                  country_code: patient.address.country_code,
              }
            : undefined,
        insurance: primary_insurance
            ? {
                  name: primary_insurance.name ?? '',
                  type: primary_insurance.type ?? '',
              }
            : undefined,
    };
};

export const removeEmptyPatientFields = (patient_data: PatientFormData): PatientFormData => {
    let entries = Object.entries(patient_data);
    entries = entries.filter(([key, value]) => {
        if (key === 'phone' && typeof value === 'string') {
            // Normalize phone and check if result is empty
            const normalized = normalizePhoneNumber(value, patient_data.country_code);
            return normalized !== '';
        }

        return Boolean(value);
    });

    return Object.fromEntries(entries) as PatientFormData;
};

export const getPatientAppointments = async (
    pvs_patient_id: string,
    eterno_patient_id: string | undefined,
    instance_id: string
): Promise<undefined | { future: Appointment[]; past: Appointment[] }> => {
    const config = getInstanceConfig(instance_id);
    const location_key = useConfigStore.getState().selected_location?.key;

    if (!config) {
        logger.error('No config found for patient instance {instanceId}', { instanceId: instance_id });
        return undefined;
    }

    const appts = await getUserAppointments(
        eterno_patient_id,
        pvs_patient_id,
        instance_id,
        config.pvs_provider,
        location_key
    );

    const all_appointments = [...appts.active, ...appts.inactive].filter((apt) => {
        return (
            apt.status === AppointmentBookStatus.COMPLETED ||
            apt.status === AppointmentBookStatus.BOOKED ||
            apt.status === AppointmentBookStatus.PENDING_BOOKING ||
            apt.status === AppointmentBookStatus.CANCELLED
        );
    });

    const now = dayjs();

    const future_appointments = all_appointments.filter((apt) => {
        return isFutureAppointment(now, apt);
    });

    const past_appointments = all_appointments.filter((apt) => {
        return isPastAppointment(now, apt);
    });

    return {
        future: future_appointments,
        past: past_appointments,
    };
};

export const getPatientName = (appointment: DailyAppointment): string => {
    return (
        appointment.data.patient_name ??
        (appointment.patient ? `${appointment.patient.name.first_name} ${appointment.patient.name.last_name}` : '')
    );
};

export const getPatientDisplayName = (appointment: DailyAppointment): string | undefined => {
    if (!appointment.patient) return appointment.data.patient_name?.trim();
    return `${appointment.patient.name.last_name} ${appointment.patient.name.first_name}`.trim();
};

// Converts an appointment to a daily appointment by enriching it with patient data
export const enrichAppointmentWithPatient = (
    appointment: Appointment | DailyAppointment,
    patient: GenericPatient
): DailyAppointment => {
    const enriched_patient = convertGenericPatientToDailyAppointmentPatient(patient, appointment);

    return {
        ...appointment,
        patient: enriched_patient,
        data: {
            ...appointment.data,
            patient_name: `${patient.personal.last_name} ${patient.personal.first_name}`,
        },
    };
};
